/*
 * © 2021 Thoughtworks, Inc.
 */

import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { equals } from 'ramda'
import moment, { unitOfTime } from 'moment'
import { renderToStaticMarkup } from 'react-dom/server'
import Chart from 'react-apexcharts'
import { useTheme } from '@mui/material/styles'
import { GetApp, PanTool, RotateLeft, ZoomIn } from '@mui/icons-material'
import { EstimationResult } from '@cloud-carbon-footprint/common'
import { ApexChartProps, DateRange } from '../../../../Types'
import { getChartColors, CCFTheme } from '../../../../utils/themes'
import { sumServiceTotals, getMaxOfDataSeries } from '../../../../utils/helpers'
import { filterBy, sortByDate } from './helpers'
import CustomTooltip from './CustomTooltip'

type LegendToggle = {
  [key: string]: boolean
}

const ApexLineChart: FunctionComponent<ApexChartProps> = ({ data }) => {
  const theme = useTheme() as CCFTheme
  const colors = getChartColors(theme)
  const [blue, yellow, green] = [colors[0], colors[5], colors[8]]

  const [dateRange, setDateRange] = React.useState<DateRange>({
    min: null,
    max: null,
  })
  const [chartData, setChartData] = React.useState<EstimationResult[]>([])
  const [defaultRange, setDefaultRange] = React.useState<DateRange>({
    min: null,
    max: null,
  })
  const [toggledSeries, setToggledSeries] = React.useState<LegendToggle[]>([
    { CO2e: true },
    { 'Kilowatt Hours': false },
    { Cost: false },
  ])

  const filteredByZoomRange = filterBy(chartData, dateRange, defaultRange)

  // We need to get the HTML string version of these icons since ApexCharts doesn't take in custom React components.
  // Why, you might ask? Don't ask me, ask ApexCharts.
  const GetAppIconHTML = renderToStaticMarkup(<GetApp />)
  const PanToolIconHTML = renderToStaticMarkup(<PanTool />)
  const RotateLeftIconHTML = renderToStaticMarkup(<RotateLeft />)
  const ZoomInIconHTML = renderToStaticMarkup(<ZoomIn />)

  const cloudEstimationData = sumServiceTotals(filteredByZoomRange)
  const co2SeriesData = cloudEstimationData.co2Series
  const kilowattHoursSeriesData = cloudEstimationData.kilowattHoursSeries
  const costSeriesData = cloudEstimationData.costSeries
  const maxCO2e = getMaxOfDataSeries(co2SeriesData)
  const maxKilowattHours = getMaxOfDataSeries(kilowattHoursSeriesData)
  const maxCost = getMaxOfDataSeries(costSeriesData)

  const grouping = data[0]?.groupBy || 'day'
  const dateFormat = {
    day: 'MMM DD, YYYY',
    week: '[Week] w, MMM',
    month: 'MMM YYYY',
    quarter: 'Qo [Quarter] YYYY',
    year: 'YYYY',
  }

  useEffect(() => {
    const newSortedData = sortByDate(data)
    const min = newSortedData[0]?.timestamp
      ? new Date(newSortedData[0]?.timestamp)
      : null
    const endDate = new Date(newSortedData[newSortedData.length - 1]?.timestamp)
    const max = endDate
      ? moment(endDate)
          .add(1, `${grouping}s` as unitOfTime.DurationConstructor)
          .toDate()
      : null
    const newDefaultRange = {
      min,
      max,
    }

    if (!equals(chartData, newSortedData)) setChartData(newSortedData)
    if (
      newDefaultRange.min instanceof Date &&
      newDefaultRange.max instanceof Date
    ) {
      if (!equals(defaultRange, newDefaultRange)) {
        setDateRange(newDefaultRange)
        setDefaultRange(newDefaultRange)
      }
    }
  }, [data])

  // Use series.hidden (declarative) instead of ApexCharts.exec show/hide — avoids races with
  // react-apexcharts mount/update that caused querySelector / convertedCatToNumeric errors.
  const lineSeries = useMemo(() => {
    const visible = toggledSeries.map(
      (legendToggle: LegendToggle) => Object.values(legendToggle)[0] as boolean,
    )
    return [
      {
        name: 'CO2e',
        data: co2SeriesData,
        hidden: !visible[0],
      },
      {
        name: 'Kilowatt Hours',
        data: kilowattHoursSeriesData,
        hidden: !visible[1],
      },
      {
        name: 'Cost',
        data: costSeriesData,
        hidden: !visible[2],
      },
    ]
  }, [co2SeriesData, kilowattHoursSeriesData, costSeriesData, toggledSeries])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options: any = {
    markers: {
      size: 5,
    },
    chart: {
      events: {
        // ApexCharts Toolbar sets `xaxis = beforeZoomRange.xaxis` when the return value is
        // truthy. Returning `{ dateRange }` made `xaxis` undefined and merged `{ xaxis: undefined }`,
        // which broke `w.config.xaxis` (convertedCatToNumeric access). Return nothing to keep the
        // computed zoom range when we only skip updating React state (< 2 points).
        beforeZoom: (chart: unknown, { xaxis }: { xaxis: DateRange }) => {
          const newFilteredData = filterBy(data, xaxis, defaultRange)

          if (newFilteredData.length >= 2) {
            setDateRange(xaxis)
            return {
              xaxis,
            }
          }
          return undefined
        },
        beforeResetZoom: () => {
          setDateRange(defaultRange)
        },
        legendClick: (chart: unknown, seriesIndex: number) => {
          const [seriesKey, toggledValue] = Object.entries(
            toggledSeries[seriesIndex],
          )[0]
          const toggleCheck = toggledSeries.filter(
            (series) => !!Object.values(series)[0],
          )
          const newToggledSeries = [...toggledSeries]
          newToggledSeries[seriesIndex] = {
            [seriesKey]: !toggledValue,
          }
          setToggledSeries(newToggledSeries)
          if (
            toggleCheck.length === 1 &&
            Object.keys(toggleCheck[0])[0] === seriesKey
          )
            setToggledSeries(toggledSeries)
        },
      },
      id: 'lineChart',
      background: theme.palette.background.paper,
      toolbar: {
        tools: {
          download: GetAppIconHTML,
          zoom: ZoomInIconHTML,
          zoomin: false,
          zoomout: false,
          pan: PanToolIconHTML,
          reset: RotateLeftIconHTML,
        },
      },
    },
    colors: [blue, yellow, green],
    height: '500px',
    stroke: {
      width: 1,
    },
    theme: {
      mode: theme.palette.mode,
    },
    tooltip: {
      shared: true,
      custom: function ({ dataPointIndex }: { dataPointIndex: number }) {
        return renderToStaticMarkup(
          <CustomTooltip
            dataPoint={co2SeriesData[dataPointIndex]}
            grouping={grouping}
          />,
        )
      },
    },
    title: {
      text: 'Cloud Usage',
      offsetY: -8,
      style: {
        fontSize: '24px',
      },
    },
    xaxis: {
      type: 'category',
      tickAmount: 'dataPoints',
      title: {
        text: '',
        offsetY: 18,
        style: {
          fontSize: '15px',
        },
      },
      labels: {
        formatter: function (val) {
          return moment(val).add(1, `d`).format(dateFormat[grouping])
        },
        style: {
          display: 'contents !important',
        },
      },
    },
    yaxis: [
      {
        max: 1.1 * maxCO2e,
        title: {
          text: 'CO2e (metric tons)',
          offsetX: -8,
          style: {
            fontSize: '15px',
          },
        },
        tickAmount: 10,
        decimalsInFloat: 3,
      },
      {
        max: 1.1 * maxKilowattHours,
        title: {
          text: 'Kilowatt Hours (kWh)',
          opposite: -8,
          style: {
            fontSize: '15px',
            color: yellow,
          },
        },
        tickAmount: 10,
        decimalsInFloat: 2,
        opposite: true,
        axisBorder: {
          show: true,
          color: yellow,
        },
        axisTicks: {
          show: false,
        },
        showAlways: false,
      },
      {
        max: 1.1 * maxCost,
        title: {
          text: 'Cost ($)',
          offsetX: 6,
          style: {
            fontSize: '15px',
            color: green,
          },
        },
        tickAmount: 10,
        decimalsInFloat: 2,
        opposite: true,
        axisBorder: {
          show: true,
          color: green,
          offsetX: -5,
        },
        axisTicks: {
          show: false,
        },
        showAlways: false,
      },
    ],
    grid: {
      padding: {
        bottom: +15,
        right: +20,
      },
    },
  }

  return (
    <Chart
      aria-label="apex-line-chart"
      options={options}
      series={lineSeries}
      type="line"
      height={options.height}
    />
  )
}

export default ApexLineChart
